import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 57. PRIVACY
// Architecture respectant le principe de MINIMISATION des données :
// les modules ne doivent accéder qu'aux données nécessaires.
// Zifek Core permet : contrôle des accès · journalisation · export lorsque
// requis · suppression lorsque applicable · gestion des consentements.
// Les exigences légales précises sont gérées selon le pays et les modules.
// ─────────────────────────────────────────────────────────────

export const PRIVACY_REQUEST_TYPES: { key: string; label: string; icon: string; desc: string }[] = [
  { key: 'access', label: 'Accès', icon: 'ri-eye-line', desc: 'Obtenir une copie des données détenues.' },
  { key: 'export', label: 'Export (portabilité)', icon: 'ri-download-2-line', desc: 'Récupérer ses données dans un format réutilisable.' },
  { key: 'rectification', label: 'Rectification', icon: 'ri-edit-2-line', desc: 'Corriger des données inexactes.' },
  { key: 'deletion', label: 'Suppression', icon: 'ri-delete-bin-6-line', desc: 'Effacer les données lorsque applicable.' },
];

export function privacyTypeMeta(key: string | null | undefined) {
  return PRIVACY_REQUEST_TYPES.find((t) => t.key === key) || PRIVACY_REQUEST_TYPES[0];
}

export const PRIVACY_STATUSES: { key: string; label: string; className: string }[] = [
  { key: 'pending', label: 'En attente', className: 'bg-accent-100 text-accent-900' },
  { key: 'in_progress', label: 'En cours', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'completed', label: 'Traitée', className: 'bg-primary-100 text-primary-900' },
  { key: 'rejected', label: 'Refusée', className: 'bg-red-100 text-red-800' },
];

export function privacyStatusMeta(key: string | null | undefined) {
  return PRIVACY_STATUSES.find((s) => s.key === key) || PRIVACY_STATUSES[0];
}

/** Règles de minimisation : ce que chaque module est autorisé à lire. */
export const DATA_MINIMIZATION_MATRIX: { module: string; allowed: string; restricted: string; icon: string }[] = [
  { module: 'Zifek Commerce', allowed: 'Commandes, articles, adresse de livraison', restricted: 'Données médicales, consentements marketing des tiers', icon: 'ri-shopping-bag-3-line' },
  { module: 'Zifek Finance', allowed: 'Factures, paiements, coordonnées de facturation', restricted: 'Préférences d’affichage, favoris', icon: 'ri-funds-line' },
  { module: 'Zifek CRM', allowed: 'Fiche client, historique d’interactions', restricted: 'Coordonnées bancaires', icon: 'ri-user-star-line' },
  { module: 'Zifek Marketing', allowed: 'Consentements marketing, email (si accordé)', restricted: 'Données financières, documents sensibles', icon: 'ri-megaphone-line' },
  { module: 'Zifek Hospitality', allowed: 'Réservations, dates de séjour, préférences de chambre', restricted: 'Historique d’achats hors séjour', icon: 'ri-hotel-line' },
];

export interface CorePrivacyRequest {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  request_type: string;
  subject_type: string | null;
  subject_id: string | null;
  requester_user_id: number | null;
  requester_name: string | null;
  requester_email: string | null;
  status: string | null;
  reason: string | null;
  result: Record<string, unknown> | null;
  handled_by: number | null;
  handled_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function listPrivacyRequests(organizationId: number): Promise<CorePrivacyRequest[]> {
  const { data, error } = await supabase
    .from('core_privacy_requests')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data || []) as CorePrivacyRequest[];
}

export interface PrivacyRequestInput {
  tenantId?: number | null;
  organizationId: number;
  requestType: string;
  subjectType?: string;
  subjectId?: string | number | null;
  requesterUserId?: number | null;
  requesterName?: string | null;
  requesterEmail?: string | null;
  reason?: string | null;
}

export async function createPrivacyRequest(input: PrivacyRequestInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_privacy_requests')
    .insert({
      tenant_id: input.tenantId ?? null,
      organization_id: input.organizationId,
      request_type: input.requestType,
      subject_type: input.subjectType || 'user',
      subject_id: input.subjectId != null ? String(input.subjectId) : null,
      requester_user_id: input.requesterUserId ?? null,
      requester_name: input.requesterName ?? null,
      requester_email: input.requesterEmail ?? null,
      status: 'pending',
      reason: input.reason || null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function handlePrivacyRequest(
  id: number,
  status: 'in_progress' | 'completed' | 'rejected',
  result?: Record<string, unknown> | null,
  handledBy?: number | null,
): Promise<void> {
  const { error } = await supabase
    .from('core_privacy_requests')
    .update({
      status,
      result: result ?? null,
      handled_by: handledBy ?? null,
      handled_at: status === 'completed' || status === 'rejected' ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Compile les données d'une personne pour une demande « accès / export ».
 * Minimisation : on ne rassemble que les données détenues par cette
 * organisation, jamais l'ensemble du système. Best-effort.
 */
export async function exportSubjectData(organizationId: number, subjectId: string | number): Promise<Record<string, unknown>> {
  const sid = String(subjectId);
  const out: Record<string, unknown> = { subject_id: sid, organization_id: organizationId, generated_at: new Date().toISOString() };
  const safe = async (label: string, fn: () => Promise<unknown>) => {
    try { out[label] = await fn(); } catch { out[label] = []; }
  };
  await safe('customers', async () => {
    const { data } = await supabase.from('core_customers').select('*').eq('organization_id', organizationId).eq('id', sid);
    return data || [];
  });
  await safe('contacts', async () => {
    const { data } = await supabase.from('core_contacts').select('*').eq('organization_id', organizationId).eq('id', sid);
    return data || [];
  });
  await safe('orders', async () => {
    const { data } = await supabase.from('core_activity_timeline').select('*').eq('organization_id', organizationId).eq('entity_id', sid).limit(200);
    return data || [];
  });
  await safe('reviews', async () => {
    const { data } = await supabase.from('core_reviews').select('*').eq('organization_id', organizationId).eq('author_user_id', sid);
    return data || [];
  });
  await safe('favorites', async () => {
    const { data } = await supabase.from('core_favorites').select('*').eq('user_id', sid);
    return data || [];
  });
  await safe('consents', async () => {
    const { data } = await supabase.from('core_consents').select('*').eq('user_id', sid);
    return data || [];
  });
  return out;
}

export interface CoreDataAccessLog {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number | null;
  module: string | null;
  resource_type: string | null;
  resource_id: string | null;
  action: string | null;
  purpose: string | null;
  created_at: string | null;
}

/** Journalise un accès à des données (contrôle + traçabilité). */
export async function logDataAccess(input: {
  tenantId?: number | null;
  organizationId: number;
  userId?: number | null;
  module: string;
  resourceType: string;
  resourceId?: string | number | null;
  action?: string;
  purpose?: string | null;
}): Promise<void> {
  try {
    await supabase.from('core_data_access_log').insert({
      tenant_id: input.tenantId ?? null,
      organization_id: input.organizationId,
      user_id: input.userId ?? null,
      module: input.module,
      resource_type: input.resourceType,
      resource_id: input.resourceId != null ? String(input.resourceId) : null,
      action: input.action || 'read',
      purpose: input.purpose || null,
    });
  } catch {
    /* best-effort */
  }
}

export async function listAccessLog(organizationId: number, limit = 100): Promise<CoreDataAccessLog[]> {
  const { data, error } = await supabase
    .from('core_data_access_log')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as CoreDataAccessLog[];
}

export interface PrivacyStats {
  total: number;
  pending: number;
  completed: number;
  byType: Record<string, number>;
  accessEvents: number;
}

export async function fetchPrivacyStats(organizationId: number): Promise<PrivacyStats> {
  const [requests, log] = await Promise.all([listPrivacyRequests(organizationId), listAccessLog(organizationId, 500)]);
  const byType: Record<string, number> = {};
  let pending = 0;
  let completed = 0;
  requests.forEach((r) => {
    byType[r.request_type] = (byType[r.request_type] || 0) + 1;
    if (r.status === 'pending' || r.status === 'in_progress') pending += 1;
    if (r.status === 'completed') completed += 1;
  });
  return { total: requests.length, pending, completed, byType, accessEvents: log.length };
}